import { useAppNavigate } from '@/lib/AppNavigatorHook';
import { getImageUrl } from '@/lib/media';
import { formatRecurrenceInterval } from '@/lib/recurrenceLabels';
import { AvailablePackage } from '../../../../services/student/packages.interfaces';

interface AvailablePackageCardProps {
  pkg: AvailablePackage;
}

const formatPrice = (price?: number) => {
  if (price === undefined || price === null || price <= 0) return null;
  return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const AvailablePackageCard = ({ pkg }: AvailablePackageCardProps) => {
  const { goToCheckout } = useAppNavigate();
  const priceLabel = formatPrice(pkg.price);
  const moduleCount = pkg.totalModules || pkg.modulesCount;
  const cycleSuffix = pkg.isRecurring
    ? formatRecurrenceInterval(pkg.recurrenceIntervalMonths)
    : "";

  const handleBuyClick = () => {
    goToCheckout(pkg.id, {
      name: pkg.name,
      modality: pkg.modality,
      level: pkg.level,
      duration: pkg.duration,
      modulesCount: moduleCount,
      discount: pkg.discount,
      imageUrl: pkg.imageUrl || pkg.image,
      price: pkg.price || 0,
      isRecurring: pkg.isRecurring,
      recurrenceIntervalMonths: pkg.recurrenceIntervalMonths,
    });
  };

  const buttonLabel = pkg.isRecurring
    ? "Assinar"
    : priceLabel
      ? "Comprar"
      : "Começar";

  return (
    <div
      className="w-full sm:max-w-[420px] h-[260px] sm:h-[280px] rounded-2xl relative overflow-hidden cursor-pointer group"
      style={{
        backgroundImage: `url(${getImageUrl(pkg.imageUrl || pkg.image)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start gap-2">
        {pkg.isRecurring ? (
          <div className="bg-white/95 text-foreground rounded-md px-2 py-1 text-xs font-semibold tracking-wide">
            Assinatura
          </div>
        ) : (
          <span />
        )}
        {pkg.discount > 0 && (
          <div className="bg-success-subtle rounded-md px-2 py-1 flex flex-col items-center justify-center">
            <div className="text-white text-sm font-semibold">{pkg.discount}%</div>
            <div className="text-white text-xs leading-none">OFF</div>
          </div>
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-black/20 transition-all duration-300 group-hover:from-black/85" />

      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex flex-col mb-3 gap-1">
          <h3 className="text-white text-lg font-medium leading-tight">
            {pkg.name}
          </h3>
          <div className="text-white/80 text-xs">
            {moduleCount > 0
              ? `${moduleCount} ${moduleCount === 1 ? 'módulo' : 'módulos'}`
              : `Duração ${pkg.duration}`}
          </div>
          {priceLabel ? (
            <div className="text-white text-base font-semibold mt-1">
              {priceLabel}
              {cycleSuffix ? (
                <span className="text-white/80 text-sm font-normal"> {cycleSuffix}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleBuyClick}
            className="bg-success-subtle hover:bg-success-subtle/90 text-white text-sm font-medium px-5 py-2 min-h-[44px] rounded-md transition-colors duration-200"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
